import { AssetMatchResponse } from '@/api/generatedApi';
import { ReportGroupAsset } from '@/hooks/api/reports/useListReportGroupAssets';
import { getAssetName } from '@/utils/assets';
import { formatMatchPercentage } from '@/utils/format';

export const NO_VISIBLE_MATCH_LABEL = 'No visible match';

// Three situations leave an asset with no matches, and a percentage would misrepresent all
// of them: the presenter filtered away partners this viewer cannot see; the asset was moved
// into the group, which writes no match rows; or the group was reduced to one asset, which
// move-asset-to-group only deletes at zero. None means "measured 0% similar", so the cell
// carries a mark rather than a number. (Moves currently need the API — the drag affordance
// exists but no droppable registers `type: 'report-group'`, so drops never resolve.)
export const NO_VISIBLE_MATCH_MARK = '\u2014';
export const NO_VISIBLE_MATCH_TOOLTIP = 'This item had no visible matches.';

// Matches arrive sorted by descending percentage and already filtered to the assets
// this viewer is allowed to see, so the first one is the closest visible match.
export const getClosestMatch = (asset: ReportGroupAsset): AssetMatchResponse | undefined => asset.matches?.[0];

export const getMatchCount = (asset: ReportGroupAsset): number => asset.matches?.length ?? 0;

// The percentage is a group member's similarity to its own closest partner, not to any
// designated asset, so on its own it is ambiguous. Both views hand this to their tooltip.
export const getClosestMatchDescription = (asset: ReportGroupAsset): string => {
  const closest = getClosestMatch(asset);
  if (!closest) {
    return NO_VISIBLE_MATCH_TOOLTIP;
  }

  return `Closest match in this group: geometry is ${formatMatchPercentage(closest.matchPercentage)}% similar to ${getAssetName(closest.asset)}.`;
};

export const orderByClosestMatch = (assets: ReportGroupAsset[]): ReportGroupAsset[] => {
  const uniqueById = new Map<string, ReportGroupAsset>();
  for (const asset of assets) {
    if (!uniqueById.has(asset.id)) uniqueById.set(asset.id, asset);
  }

  return [...uniqueById.values()].sort(
    (a, b) => (getClosestMatch(b)?.matchPercentage ?? -1) - (getClosestMatch(a)?.matchPercentage ?? -1)
  );
};
